import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import api from '../lib/api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PAGE_CONTENT_MAX_CLASS } from '../lib/pageLayout';
import { useAuthContext } from '../contexts/AuthContext';
import { isOrgAdmin } from '../lib/orgRoles';

interface Goal {
  id: string;
  label: string;
  description: string;
  enabled?: boolean;
  isPlatform?: boolean;
  orgId?: string | null;
}

export function GoalsPage() {
  const { user } = useAuthContext();
  const isAdmin = isOrgAdmin(user?.orgRole);

  const [goals, setGoals] = useState<Goal[]>([]);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [goalIdToDelete, setGoalIdToDelete] = useState<string | null>(null);

  const fetchGoals = useCallback(() => {
    const endpoint = isAdmin ? '/orgs/me/goals?includeDisabled=true' : '/orgs/me/goals';
    return api
      .get(endpoint)
      .then((res) => setGoals(res.data))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    void fetchGoals();
  }, [fetchGoals]);

  const toggleGoal = async (goalId: string, currentlyEnabled: boolean) => {
    try {
      if (currentlyEnabled) {
        await api.delete(`/orgs/me/goals/${goalId}`);
      } else {
        await api.post('/orgs/me/goals', { goalId });
      }
      setGoals((prev) =>
        prev.map((g) => (g.id === goalId ? { ...g, enabled: !currentlyEnabled } : g)),
      );
    } catch (err) {
      console.error('Failed to toggle goal', err);
      Sentry.captureException(err);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    try {
      await api.post('/practice/goals', { label: label.trim(), description: description.trim() });
      setLabel('');
      setDescription('');
      fetchGoals();
    } catch {
      // handled by api interceptor
    }
  };

  const confirmDelete = async () => {
    if (!goalIdToDelete) return;
    const id = goalIdToDelete;
    setGoalIdToDelete(null);
    try {
      await api.delete(`/practice/goals/${id}`);
      fetchGoals();
    } catch {
      // handled
    }
  };

  const startEdit = (goal: Goal) => {
    setEditingId(goal.id);
    setEditLabel(goal.label);
    setEditDescription(goal.description);
  };

  const handleUpdate = async () => {
    if (!editingId || !editLabel.trim()) return;
    try {
      await api.put(`/practice/goals/${editingId}`, {
        label: editLabel.trim(),
        description: editDescription.trim(),
      });
      setEditingId(null);
      fetchGoals();
    } catch {
      // handled
    }
  };

  const platformGoals = goals.filter((g) => g.isPlatform);
  const orgGoals = goals.filter((g) => !g.isPlatform);

  return (
    <div className={PAGE_CONTENT_MAX_CLASS}>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Practice Goals</h1>
      <p className="text-gray-600 mb-8">
        {isAdmin
          ? 'Enable platform goals for your team, and add organization-specific goals. Sessions can be evaluated against the selected goal.'
          : 'Goals available for practice. Admins can enable platform goals and add custom goals for your organization.'}
      </p>

      {!isAdmin && (
        <div className="space-y-8 mb-8">
          {goals.length === 0 ? (
            <p className="text-gray-500">No goals are enabled yet. Ask an admin to enable goals.</p>
          ) : (
            <>
              {orgGoals.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">
                    Your organization&apos;s goals
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {orgGoals.map((goal) => (
                      <div
                        key={goal.id}
                        className="bg-white p-4 rounded-lg border border-green-200 text-left"
                      >
                        <div className="font-medium text-gray-900">{goal.label}</div>
                        {goal.description ? (
                          <div className="text-sm text-gray-600 mt-1">{goal.description}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {platformGoals.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Platform catalog</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {platformGoals.map((goal) => (
                      <div
                        key={goal.id}
                        className="bg-white p-4 rounded-lg border border-green-200"
                      >
                        <div className="font-medium text-gray-900">{goal.label}</div>
                        {goal.description ? (
                          <div className="text-sm text-gray-600 mt-1">{goal.description}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isAdmin && (
        <>
          <div className="bg-white rounded-lg border mb-8">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Platform catalog</h2>
              <p className="text-sm text-gray-500 mt-1">
                Toggle which preset goals appear when your team starts a practice session.
              </p>
            </div>
            {platformGoals.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No platform goals in the catalog yet.</div>
            ) : (
              <ul className="divide-y">
                {platformGoals.map((goal) => (
                  <li
                    key={goal.id}
                    className="p-4 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 break-words">{goal.label}</div>
                      {goal.description ? (
                        <div className="text-sm text-gray-500 mt-0.5 break-words">
                          {goal.description}
                        </div>
                      ) : null}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 shrink-0 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-green-700 focus:ring-green-600"
                        checked={goal.enabled ?? false}
                        onChange={() => void toggleGoal(goal.id, goal.enabled ?? false)}
                      />
                      Enabled for my org
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form onSubmit={handleAdd} className="bg-white rounded-lg border p-4 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Add organization goal</h2>
            <div className="space-y-3">
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Goal label (e.g. Book a demo)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                type="submit"
                disabled={!label.trim()}
                className="bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
              >
                Add goal
              </button>
            </div>
          </form>

          {orgGoals.length === 0 ? (
            <div className="bg-white p-8 rounded-lg border text-center text-sm text-gray-500">
              No organization-specific goals yet. Add one above.
            </div>
          ) : (
            <div className="bg-white rounded-lg border divide-y mb-8">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">
                  Your organization&apos;s goals
                </h2>
              </div>
              {orgGoals.map((goal) => (
                <div key={goal.id} className="p-4">
                  {editingId === goal.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => void handleUpdate()}
                          className="bg-green-700 text-white px-3 py-1 rounded-md text-sm hover:bg-green-800 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 break-words">{goal.label}</div>
                        {goal.description ? (
                          <div className="text-sm text-gray-500 mt-0.5 break-words">
                            {goal.description}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-green-700 focus:ring-green-600"
                            checked={goal.enabled ?? false}
                            onChange={() => void toggleGoal(goal.id, goal.enabled ?? false)}
                          />
                          Enabled
                        </label>
                        <button
                          type="button"
                          onClick={() => startEdit(goal)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setGoalIdToDelete(goal.id)}
                          className="text-sm text-red-500 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={goalIdToDelete !== null}
        title="Delete organization goal?"
        description="Delete this organization goal? It will be removed for your team and cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setGoalIdToDelete(null)}
      />
    </div>
  );
}
